import { Grid } from '../../core/Grid.js';
import {
    Position,
    Direction,
    Edge,
    positionKey,
    parsePositionKey,
    edgeKey,
    edgeAdjacentCells
} from '../../core/GridGeometry.js';
import { WallsGame } from './WallsGame.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class WallsVortex extends WallsGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.cachedPath = null;
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, paired) => {
            if (this.grid.hasDigitAt(p)) return false;
            if (this.grid.hasDigitAt(paired)) return false;
            if (this.hasWallOnEdges(p) || this.hasWallOnEdges(paired)) return false;
            return true;
        });
    }

    /** Check if any arrow wall is on edges adjacent to the given cell */
    private hasWallOnEdges(pos: Position): boolean {
        const walls = this.getWalls();
        const directions: Direction[] = ['N', 'E', 'S', 'W'];
        for (const dir of directions) {
            const edge = this.grid.getEdgeFromPosition(pos, dir);
            if (walls.has(edgeKey(edge))) return true;
        }
        return false;
    }

    toggleVortexGate(pos: Position): boolean {
        // A gate toggle reroutes the block just as a wall does, so it retires a
        // start override that would hide a solved loop (see WallsGame.onBoardChanged).
        return this.gm.toggleVortexGate(pos, this.canActivateGate(pos), () => {
            this.onBoardChanged();
        });
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Scoring ────────────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        if (!this.checkWin()) return null;

        const path = this.getPath();
        if (!path) return null;

        const wallCount = this.getWalls().size;

        const emptyCells = new Set<string>();
        for (const pos of path.positions) {
            if (!this.grid.hasDigitAt(pos)) {
                emptyCells.add(positionKey(pos));
            }
        }

        return calculateScore(wallCount, this.gm.deduplicateCellCount(emptyCells));
    }

    // ── Placement Blocking ────────────────────────────────────────

    override isValidEdge(edge: Edge): boolean {
        if (!super.isValidEdge(edge)) return false;
        // Block walls on edges of vortex gate cells
        const adjacentCells = edgeAdjacentCells(edge);
        return !adjacentCells.some(c => this.gm.raw.has(positionKey(c)));
    }

    // ── Teleportation ─────────────────────────────────────────────

    protected override onBlockEntersCell(
        cell: Position, justTeleported: boolean
    ): { cell: Position; teleported: boolean } {
        if (!this.gm.hasActiveGates() || justTeleported) {
            return { cell, teleported: false };
        }

        const key = positionKey(cell);
        const pairedKey = this.gm.raw.get(key);
        if (pairedKey) {
            return { cell: parsePositionKey(pairedKey), teleported: true };
        }
        return { cell, teleported: false };
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);
        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);
        this.cachedPath = null;
    }
}
